import { useState } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard,
  Factory,
  FlaskConical,
  Layers,
  Sparkles,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import { useSetPageHeader } from "@/components/layout/PageHeaderContext";
import { KPICard } from "@/components/dashboard/KPICard";
import { FilterBar } from "@/components/dashboard/FilterBar";
import { useProductionSummary } from "@/hooks/use-production";
import { useMaterialVariance } from "@/hooks/use-materials";
import { useAlertsSummary } from "@/hooks/use-analytics";
import { formatNumber, formatPct, daysAgo, today } from "@/lib/utils";

export function Overview() {
  useSetPageHeader({
    title: "Overview",
    subtitle: "Plant-wide KPIs",
    icon: LayoutDashboard,
  });

  const [from, setFrom] = useState(() => daysAgo(90));
  const [to, setTo] = useState(() => today());

  const production = useProductionSummary(from, to);
  const materials = useMaterialVariance(from, to);
  const alerts = useAlertsSummary(from, to);

  const p = production.data;
  const a = alerts.data?.totals;

  // Calculate overall material variance %
  const avgVariance =
    materials.data && materials.data.length > 0
      ? materials.data.reduce((sum, m) => sum + Math.abs(m.avg_variance_pct), 0) /
        materials.data.length
      : 0;

  return (
    <div className="space-y-6">
      <FilterBar from={from} to={to} onFromChange={setFrom} onToChange={setTo} />

      {/* Main KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KPICard
          title="Daily Output"
          value={p ? formatNumber(p.avg_daily_output) : "..."}
          subtitle="avg parts per day"
          status={p && p.avg_daily_output > 500 ? "green" : "amber"}
          icon={<Factory className="h-5 w-5" />}
        />
        <KPICard
          title="Total Production"
          value={p ? formatNumber(p.total_quantity) : "..."}
          subtitle={p ? `${formatNumber(p.total_runs)} runs` : ""}
          icon={<Factory className="h-5 w-5" />}
        />
        <KPICard
          title="Material Variance"
          value={formatPct(avgVariance)}
          subtitle="avg absolute variance"
          status={avgVariance < 2 ? "green" : avgVariance < 5 ? "amber" : "red"}
          icon={<Layers className="h-5 w-5" />}
          href="/app/materials"
        />
        <KPICard
          title="Quality Alerts"
          value={a ? formatNumber(a.critical + a.warning + a.at_risk) : "..."}
          subtitle={a ? `${a.critical} critical, ${a.at_risk} at risk` : ""}
          status={
            a
              ? a.critical > 0
                ? "red"
                : a.at_risk > 0
                  ? "amber"
                  : "green"
              : "neutral"
          }
          icon={<AlertTriangle className="h-5 w-5" />}
          href="/app/insights"
        />
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <QuickLink
          to="/app/production"
          icon={<Factory className="h-5 w-5" />}
          title="Production"
          description="Run throughput & trends"
          stat={p ? `${formatNumber(p.total_runs)} runs` : "Loading..."}
        />
        <QuickLink
          to="/app/quality"
          icon={<FlaskConical className="h-5 w-5" />}
          title="Quality Control"
          description="SPC charts & spec limits"
          stat="Control charts"
        />
        <QuickLink
          to="/app/materials"
          icon={<Layers className="h-5 w-5" />}
          title="Materials"
          description="Target vs actual variance"
          stat={
            materials.data
              ? `${materials.data.length} materials tracked`
              : "Loading..."
          }
        />
        <QuickLink
          to="/app/insights"
          icon={<Sparkles className="h-5 w-5" />}
          title="Insights"
          description="Aggregated quality signals"
          stat={
            a
              ? `${a.products_scanned} products scanned`
              : "Loading..."
          }
        />
      </div>
    </div>
  );
}

function QuickLink({
  to,
  icon,
  title,
  description,
  stat,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  stat: string;
}) {
  return (
    <Link
      to={to}
      className="group flex flex-col justify-between rounded-xl border border-border bg-card p-5 transition-all hover:border-primary/30 hover:shadow-md"
    >
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary">
          {icon}
        </div>
        <div>
          <h3 className="font-semibold text-card-foreground">{title}</h3>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{stat}</span>
        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
      </div>
    </Link>
  );
}
